/**
 * SXO — Arama Deneyimi Optimizasyonu (bağımsız özellik).
 * Ana ekran bir skor değil FIRSAT LİSTESİ: otoritesi yüksek ama SXO'su düşük
 * sayfalar, eksikleriyle; her satırdan tek tıkla Sayfa Güçlendirme'ye.
 */
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Jargon } from "@/components/ui/jargon";
import { Gauge, Zap, Loader2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const C = { green: "#12b76a", red: "#d92d20", amber: "#f79009", blue: "#2970ff" };

type Firsat = { pageId: number; url: string; tip: string; ilr: number; sxo: number; kirilim: { niyet: number | null; deneyim: number | null; donusum: number | null }; eksikler: string[] };
type Veri = { site: number | null; deneyimKaynak: string | null; sayfaSayisi: number; firsatlar: Firsat[] };

const TIP_AD: Record<string, string> = { hizmet: "Hizmet", blog: "Blog", iletisim: "İletişim", kurumsal: "Kurumsal", anasayfa: "Ana sayfa" };
const renk = (n: number | null) => (n == null ? "#9ca3af" : n >= 80 ? C.green : n >= 55 ? C.amber : C.red);
const yol = (u: string) => u.replace(/^https?:\/\/[^/]+/, "") || "/";

export default function Sxo() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { data: d, isLoading } = useQuery<Veri>({
    queryKey: ["sxo", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/sxo`);
      if (!r.ok) throw new Error("sxo");
      return r.json();
    },
  });

  type SirKol = "kazanc" | "ilr" | "sxo";
  const [sira, setSira] = useState<{ k: SirKol; azalan: boolean }>({ k: "kazanc", azalan: true });
  const sirala = (k: SirKol) => setSira((o) => (o.k === k ? { k, azalan: !o.azalan } : { k, azalan: true }));
  const siraliFirsatlar = useMemo(() => {
    const f = [...(d?.firsatlar ?? [])];
    const deger = (x: Firsat) => (sira.k === "kazanc" ? (100 - x.sxo) * (x.ilr + 10) : sira.k === "ilr" ? x.ilr : x.sxo);
    f.sort((a, b) => (sira.azalan ? deger(b) - deger(a) : deger(a) - deger(b)));
    return f;
  }, [d?.firsatlar, sira]);
  const Ok = ({ k }: { k: SirKol }) => (sira.k === k ? (sira.azalan ? <ChevronDown className="inline h-3 w-3" /> : <ChevronUp className="inline h-3 w-3" />) : null);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/10"><Gauge className="h-6 w-6 text-brand-500" /></div>
        <div>
          <h1 className="text-xl font-bold"><Jargon k="sxo" metin="SXO" /> — Arama Deneyimi</h1>
          <p className="text-sm text-muted-foreground">Trafik alan ama ziyaretçiyi dönüştüremeyen sayfalar: niyet uyumu + hız deneyimi + dönüşüm çağrısı, sayfa sayfa.</p>
        </div>
        {d?.site != null && (
          <div className="ml-auto text-right">
            <div className="text-3xl font-bold tabular-nums" style={{ color: renk(d.site) }}>{d.site}<span className="text-sm text-muted-foreground">/100</span></div>
            <div className="text-[11px] text-muted-foreground">{d.sayfaSayisi} sayfa · otorite ağırlıklı{d.deneyimKaynak === "lab" ? " · CWV: laboratuvar" : d.deneyimKaynak === "alan" ? " · CWV: gerçek kullanıcı" : ""}</div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Sayfalar hesaplanıyor…</div>
      ) : !d || d.sayfaSayisi === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Hesap için taranmış sayfa gerekiyor — önce siteyi tarat. CWV ölçümü de (Nöbetçi → Şimdi ölç) deneyim bacağını doldurur.</CardContent></Card>
      ) : d.firsatlar.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm">Tüm sayfaların SXO'su 75+ — dönüşüm kancaların yerinde. 👏</CardContent></Card>
      ) : (
        <Card><CardContent className="p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><AlertTriangle className="h-4 w-4" style={{ color: C.amber }} /> Fırsat Listesi <span className="text-[11px] font-normal text-muted-foreground">— otoritesi yüksek ama deneyimi zayıf {d.firsatlar.length} sayfa, kazanç sırasıyla</span></div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-[12.5px]">
              <thead><tr className="border-b-2 border-gray-100 text-left dark:border-gray-800">
                <th className="cursor-pointer select-none px-2 py-2 font-semibold" onClick={() => sirala("kazanc")} title="Varsayılan: kazanç sıralaması">Sayfa <Ok k="kazanc" /></th>
                <th className="px-2 py-2">Tip</th>
                <th className="cursor-pointer select-none px-2 py-2 text-center" onClick={() => sirala("ilr")} title="Sıralamak için tıkla">Otorite <Ok k="ilr" /></th>
                <th className="cursor-pointer select-none px-2 py-2 text-center" onClick={() => sirala("sxo")} title="Sıralamak için tıkla">SXO <Ok k="sxo" /></th>
                <th className="px-2 py-2 text-center" title="Niyet / Deneyim / Dönüşüm">N · D · Dö</th>
                <th className="px-2 py-2">Eksikler</th>
                <th className="px-2 py-2" />
              </tr></thead>
              <tbody>
                {siraliFirsatlar.map((f) => (
                  <tr key={f.pageId} className="border-b border-gray-50 align-top dark:border-gray-800/60">
                    <td className="max-w-[240px] truncate px-2 py-2 font-mono text-[11.5px]" title={f.url}>{yol(f.url)}</td>
                    <td className="px-2 py-2 text-[11.5px] text-muted-foreground">{TIP_AD[f.tip] ?? f.tip}</td>
                    <td className="px-2 py-2 text-center tabular-nums">{f.ilr}</td>
                    <td className="px-2 py-2 text-center"><span className="font-bold tabular-nums" style={{ color: renk(f.sxo) }}>{f.sxo}</span></td>
                    <td className="px-2 py-2 text-center tabular-nums text-[11.5px]">
                      {([f.kirilim.niyet, f.kirilim.deneyim, f.kirilim.donusum] as const).map((v, i) => (
                        <span key={i} style={{ color: renk(v) }}>{v ?? "—"}{i < 2 ? " · " : ""}</span>
                      ))}
                    </td>
                    <td className="max-w-[320px] px-2 py-2 text-[11.5px] text-muted-foreground">{f.eksikler.join(" ") || "—"}</td>
                    <td className="px-2 py-2">
                      <Link href={`/page-strengthen/${f.pageId}`}><Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]"><Zap className="h-3 w-3" /> Güçlendir</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">N=Niyet (hedef kelime kapsaması) · D=Deneyim (Core Web Vitals) · Dö=Dönüşüm (sayfa tipine göre çağrı/iç link beklentisi). Sıralama: (100−SXO) × otorite — kaybın en büyük olduğu sayfa en üstte.</p>
        </CardContent></Card>
      )}
    </div>
  );
}
